import React from "react";

import { TransitionProps } from "@mui/material/transitions";
import { GetAllEventsQuery, useGetExternalEntityMapQuery } from "./api/events.generated";

import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  useTheme
} from "@mui/material";
import { RenderNativeRequests, RenderObject } from "./RenderObject";


type Event = GetAllEventsQuery["getAllEvents"]["items"][0];

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const rows: { label: string, key: keyof Event, Renderer?: React.FC<{ row: Event, displayName?: string }> }[] = [
  { label: "Created", key: "createdAt" },
  { label: "Method", key: "method" },
  { label: "UserID", key: "userId", Renderer: ({ row, displayName }) => (
    `${row.userId}${displayName ? ` (${displayName})` : ""}`
  ) },
  { label: "Path", key: "path" },
  { label: "Duration", key: "duration" },
  { label: "HTTP Status", key: "httpStatus" },
  { label: "Entity Type", key: "entityType" },
  { label: "Identifier", key: "identifier" },
];

type Metadata = { headers?: object }

const Row: React.FC<{ row: Event }> = ({ row }) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [open, setOpen] = React.useState(false);
  const { headers } = (row.metadata ? JSON.parse(row.metadata) : {}) as Metadata;
  const externalEntityMapResult = useGetExternalEntityMapQuery({
    id: `IO|${row.userId}|EMPLOYEE`
  });
  const displayName = externalEntityMapResult.data?.getExternalEntityMap?.items?.[0]?.metadata?.displayName;

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        {rows.map((el) => (
          <TableCell key={el.key} sx={{ color: row.httpStatus === 200 ? null : colors.error.dark }}>
            {el.Renderer ? <el.Renderer row={row} displayName={displayName} /> : row[el.key]}
          </TableCell>
        ))}
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            <ZoomInIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ padding: 0 }} colSpan={rows.length + 1}>
          <Dialog
            fullScreen
            open={open}
            PaperProps={{ sx: { p: "0" } }}
            TransitionComponent={Transition}
            onClose={() => setOpen(false)}
          >
            <AppBar sx={{ position: "relative", p: 0 }} color="transparent">
              <Toolbar sx={{ p: 0 }} disableGutters>
                <IconButton
                  sx={{ ml: 0 }}
                  edge="start"
                  color="inherit"
                  onClick={() => setOpen(false)}
                >
                  <CloseIcon />
                </IconButton>
                <Table>
                  <TableHead>
                    <TableRow>
                      {rows.map((el) => (
                        <TableCell key={el.key} sx={{ color: row.httpStatus === 200 ? null : colors.error.dark }}>
                          {el.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {rows.map((el) => (
                        <TableCell key={el.key} sx={{ color: row.httpStatus === 200 ? null : colors.error.dark }}>
                          {el.Renderer ? <el.Renderer row={row} displayName={displayName} /> : row[el.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Toolbar>
            </AppBar>
            <Box sx={{
              padding: "5px",
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "5px",
            }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "auto" }}>
                <Typography variant="h4">Customer</Typography>
                {row.customer ? <RenderObject payload={JSON.stringify(row.customer)} /> : "No data"}
                <Typography variant="h4">UserID</Typography>
                {row.userId
                  ? <RenderObject
                    payload={JSON.stringify(`${row.userId}${displayName ? ` (${displayName})` : ""}`)}
                  />
                  : "No data"}
                <Typography variant="h4">EventID</Typography>
                {row.eventId ? <RenderObject payload={JSON.stringify(row.eventId)} /> : "No data"}
                <Typography variant="h4">Headers</Typography>
                {headers ? <RenderObject payload={JSON.stringify(headers)} /> : "No data"}
              </Box>
              <Box sx={{ overflow: "auto" }}>
                <Typography variant="h4">Request</Typography>
                {row.body ? <RenderObject payload={row.body} /> : "No data"}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "auto" }}>
                <Typography variant="h4">Response</Typography>
                {row.response ? <RenderObject payload={row.response} /> : "No data"}
                {row.nativeRequests && (
                  <>
                    <Typography variant="h4">Native requests</Typography>
                    <RenderNativeRequests payload={row.nativeRequests} />
                  </>
                )}
              </Box>
            </Box>
          </Dialog>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default Row;
